import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const inputCls =
  "bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#E8954A] focus-visible:border-[#E8954A] rounded-sm h-11";

export default function RequestDialog({ open, onOpenChange, project }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [requestType, setRequestType] = useState("Screenplay");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "", company: "", role: "", message: "" });
    setRequestType("Screenplay");
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/requests", {
        ...form,
        project_slug: project.slug,
        project_title: project.title,
        request_type: requestType,
      });
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent
        data-testid="request-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white sm:max-w-lg rounded-sm"
      >
        {done ? (
          <div className="py-8 text-center" data-testid="request-success">
            <CheckCircle2 className="w-14 h-14 text-[#5FB85F] mx-auto mb-5" strokeWidth={1.6} />
            <h3 className="font-display text-2xl font-bold mb-2">Request Received</h3>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm mx-auto">
              Thank you for your interest in <span className="text-white">{project?.title}</span>. The Iron Fire team will
              review your request and reach out to <span className="text-white">{form.email}</span> directly.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="request-success-close"
              className="mt-7 bg-white text-black text-sm font-semibold px-6 py-2.5 rounded-sm hover:bg-[#E8954A] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8954A]">{project?.title}</p>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight">Request Materials</DialogTitle>
              <DialogDescription className="text-white/55">
                Tell us a little about you. Materials are shared with verified industry professionals.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="request-type-screenplay"
                  onClick={() => setRequestType("Screenplay")}
                  className={`py-3 rounded-sm text-sm font-semibold border transition-all ${
                    requestType === "Screenplay"
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-white/70 border-white/15 hover:border-white/40"
                  }`}
                >
                  Screenplay
                </button>
                <button
                  type="button"
                  data-testid="request-type-pitch"
                  onClick={() => setRequestType("Full Pitch")}
                  className={`py-3 rounded-sm text-sm font-semibold border transition-all ${
                    requestType === "Full Pitch"
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-white/70 border-white/15 hover:border-white/40"
                  }`}
                >
                  Full Pitch Deck
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs">Full Name *</Label>
                  <Input data-testid="request-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Producer" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs">Email *</Label>
                  <Input data-testid="request-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@studio.com" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs">Company / Studio</Label>
                  <Input data-testid="request-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Studio name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs">Role</Label>
                  <Input data-testid="request-role" value={form.role} onChange={set("role")} className={inputCls} placeholder="Producer, Director..." />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Message (optional)</Label>
                <Textarea
                  data-testid="request-message"
                  value={form.message}
                  onChange={set("message")}
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#E8954A] focus-visible:border-[#E8954A] rounded-sm min-h-[88px]"
                  placeholder="A note about your interest..."
                />
              </div>

              <button
                type="submit"
                data-testid="request-submit"
                disabled={loading}
                className="w-full bg-white text-black font-semibold py-3.5 rounded-sm hover:bg-[#E8954A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Sending..." : `Request ${requestType}`}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
